import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { GlobalVariablesService } from '../../../domain/services/admin/global-variables.service';

@Component({
  selector: 'app-global-variables',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
  ],
  templateUrl: './global-variables.component.html',
  styleUrl: './global-variables.component.css',
})
export class GlobalVariablesComponent implements OnInit {
  form!: FormGroup;

  readonly usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming',
  ];

  private readonly mockDefaults: Record<string, any> = {
    platformLogo: 'ken-burton.jpeg',
    countyName: 'Manatee County Tax Collector',
    taxCollectorName: 'The Honorable Ken Burton Jr',
    taxCollectorPhone: '850-406-8918',
    signatureImage: 'ken-burton.jpeg',
    streetAddress: '101 3rd Ave W',
    aptSuite: 'Suite 240',
    city: 'Bradenton',
    state: 'Florida',
    zipCode: '34205',
    websiteUrl: 'www.taxcollector.com',
    appraiserUrl: 'www.manateepao.gov',
    taxGroupEmail: 'touristtax.@taxcollector.com',
    paymentUnder: 5.00,
    paymentOver: 10.00,
    addressReviewCycle: 5,
    elapsedDaysVoids: 1,
    annualPurgeAlert: new Date(2024, 9, 1),
    generateHolidays: new Date(2024, 11, 1),
    generalDocImage: 'ken-burton.jpeg',
    noReplyEmail: 'noreplay.@taxcollector.com',
    replyEmail: 'noreplay.@taxcollector.com',
    staffAlertEmail: '',
    autoGenerateOnActivation: 'false',
    certCountyName: 'Manatee',
    certTaxCollectorName: 'Manatee County Tax Collector',
  };

  // Maps form-control names (camelCase) to backend global_variable keys (snake_case).
  private readonly keyMap: Record<string, string> = {
    platformLogo: 'platform_logo',
    countyName: 'county_name',
    taxCollectorName: 'tax_collector_name',
    taxCollectorPhone: 'tax_collector_phone',
    signatureImage: 'signature_image',
    streetAddress: 'street_address',
    aptSuite: 'apt_suite',
    city: 'city',
    state: 'state',
    zipCode: 'zip_code',
    websiteUrl: 'website_url',
    appraiserUrl: 'appraiser_url',
    taxGroupEmail: 'tax_group_email',
    paymentUnder: 'payment_threshold_under',
    paymentOver: 'payment_threshold_over',
    addressReviewCycle: 'address_review_cycle',
    elapsedDaysVoids: 'elapsed_days_voids',
    annualPurgeAlert: 'annual_purge_alert',
    generateHolidays: 'generate_holidays',
    generalDocImage: 'general_doc_image',
    noReplyEmail: 'no_reply_email',
    replyEmail: 'reply_email',
    staffAlertEmail: 'staff_alert_email',
    autoGenerateOnActivation: 'auto_generate_on_activation',
    certCountyName: 'cert_county_name',
    certTaxCollectorName: 'cert_tax_collector_name',
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly router: Router,
    private readonly globalVariablesService: GlobalVariablesService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/admin/global-variables', label: 'Administrative' },
      { icon: 'public', url: '/app/admin/global-variables', label: 'Global Variables' },
    ]);

    this.form = this.fb.group({
      platformLogo: [this.mockDefaults['platformLogo']],
      countyName: [this.mockDefaults['countyName']],
      taxCollectorName: [this.mockDefaults['taxCollectorName']],
      taxCollectorPhone: [this.mockDefaults['taxCollectorPhone']],
      signatureImage: [this.mockDefaults['signatureImage']],
      streetAddress: [this.mockDefaults['streetAddress']],
      aptSuite: [this.mockDefaults['aptSuite']],
      city: [this.mockDefaults['city']],
      state: [this.mockDefaults['state']],
      zipCode: [this.mockDefaults['zipCode']],
      websiteUrl: [this.mockDefaults['websiteUrl']],
      appraiserUrl: [this.mockDefaults['appraiserUrl']],
      taxGroupEmail: [this.mockDefaults['taxGroupEmail']],
      paymentUnder: [this.mockDefaults['paymentUnder']],
      paymentOver: [this.mockDefaults['paymentOver']],
      addressReviewCycle: [this.mockDefaults['addressReviewCycle']],
      elapsedDaysVoids: [this.mockDefaults['elapsedDaysVoids']],
      annualPurgeAlert: [this.mockDefaults['annualPurgeAlert']],
      generateHolidays: [this.mockDefaults['generateHolidays']],
      generalDocImage: [this.mockDefaults['generalDocImage']],
      noReplyEmail: [this.mockDefaults['noReplyEmail']],
      replyEmail: [this.mockDefaults['replyEmail']],
      staffAlertEmail: [this.mockDefaults['staffAlertEmail']],
      autoGenerateOnActivation: [this.mockDefaults['autoGenerateOnActivation']],
      certCountyName: [this.mockDefaults['certCountyName']],
      certTaxCollectorName: [this.mockDefaults['certTaxCollectorName']],
    });

    this.globalVariablesService.getAll()
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        if (data) {
          const patch: Record<string, any> = {};
          Object.keys(this.form.controls).forEach((key) => {
            const dbKey = this.keyMap[key] ?? key;
            if (data[dbKey] !== undefined && data[dbKey] !== null) {
              patch[key] = data[dbKey];
            }
          });
          this.form.patchValue(patch);
        }
      });
  }

  onSave(): void {
    if (!this.form.valid) {
      return;
    }
    const data: Record<string, string> = {};
    Object.keys(this.form.value).forEach((key) => {
      const val = this.form.value[key];
      const dbKey = this.keyMap[key] ?? key;
      data[dbKey] = val instanceof Date ? val.toISOString().split('T')[0] : String(val ?? '');
    });
    this.globalVariablesService.saveAll(data).subscribe({
      next: () => this.snackBar.open('Global variables saved.', 'Dismiss', { duration: 3000 }),
      error: (err) => {
        console.error('Error saving global variables:', err);
        this.snackBar.open('Failed to save global variables.', 'Dismiss', { duration: 5000 });
      },
    });
  }

  // Images live in global_variable.variable_value (TEXT, 64 KB) as a data URL, so the
  // file must stay small once base64-encoded. Logos and signatures fit easily.
  private static readonly MAX_IMAGE_BYTES = 45 * 1024;

  isDataImage(ctrl: string): boolean {
    return String(this.form.get(ctrl)?.value ?? '').startsWith('data:image/');
  }

  imageLabel(ctrl: string): string {
    return this.isDataImage(ctrl) ? 'Uploaded image' : (this.form.get(ctrl)?.value || 'No image');
  }

  onImagePicked(event: Event, ctrl: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > GlobalVariablesComponent.MAX_IMAGE_BYTES) {
      this.snackBar.open('Image is too large. Use a PNG or JPEG under 45 KB.', 'Dismiss', { duration: 5000 });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.form.get(ctrl)?.setValue(reader.result as string);
      this.form.markAsDirty();
    };
    reader.readAsDataURL(file);
  }

  goBack(): void {
    this.router.navigate(['/app/admin']);
  }
}
